<script setup>
import { computed, useSlots, watchEffect } from "vue";
import Dialog from "primevue/dialog";
import BaseModalPanel from "@/Components/UI/modals/BaseModalPanel.vue";
import cn from "@/helpers/cn.js";
import modalTeleportTarget from "@/Components/UI/modals/modalTeleportTarget.js";

const props = defineProps({
    show: { type: Boolean, default: false },
    // allowed: sm|md|lg|xl|2xl|3xl|4xl
    maxWidth: { type: String, default: "2xl", validator: (v) => ["sm", "md", "lg", "xl", "2xl", "3xl", "4xl"].includes(v) },
    closeable: { type: Boolean, default: true },
    centeredY: { type: Boolean, default: false },
    // Modal title. When set (or the #title slot is filled), the modal renders a real
    // header row (title left, close × right, aligned) instead of floating the × in the
    // corner over content. The old title-inside-content pattern is being retired.
    title: { type: String, default: null },
    // Confirmation-style modals render their title centered in the body (under the
    // artwork), so the header row holds only the close ×. Same chrome otherwise.
    titleInBody: { type: Boolean, default: false },
    class: String,
    // null falls through to modalTeleportTarget() — see BaseModal.vue's Teleport
    // usage below, and the module for why (WordPress Tailwind scoping).
    teleportTo: { type: String, default: null },
    ariaLabel: { type: String, default: null },
    ariaLabelledby: { type: String, default: null },
});

const emit = defineEmits(["close"]);

const close = () => {
    if (props.closeable) emit("close");
};

const slots = useSlots();
const hasTitle = computed(() => Boolean(props.title || slots.title));

// Every modal owns a header row (title + aligned ×); the title-less layout was removed.
// Warn in dev if a visible modal has no title so it's caught before shipping.
if (import.meta.env.DEV) {
    watchEffect(() => {
        if (props.show && !hasTitle.value && !props.titleInBody) {
            console.warn("[BaseModal] a title is required — pass the `title` prop or fill the #title slot.");
        }
    });
}

// Legacy width map preserved 1:1 so all 15 callers keep their panel widths.
const maxWidthClass = computed(
    () =>
        ({
            sm: "md:max-w-sm",
            md: "md:max-w-md",
            lg: "md:max-w-lg",
            xl: "md:max-w-xl",
            "2xl": "md:max-w-2xl",
            "3xl": "md:max-w-3xl",
            "4xl": "md:max-w-4xl",
        })[props.maxWidth],
);

// Width/max-width/centering live on the Dialog ROOT (.p-dialog, pt.root) — the flex
// item inside the mask. With the #container slot PrimeVue still wraps our panel in
// .p-dialog, which has no intrinsic width and shrink-wraps to content; a w-full on
// the inner panel then only fills that shrunk box (narrow on mobile where no
// max-width applies). Sizing .p-dialog directly makes mobile go full-width (minus
// the mask's gap) and desktop honour the max-width — same lesson as BaseSidebar/Drawer.
// Modals are top-anchored on every viewport (mask is items-start + md:py-15 gap).
// The legacy centeredY vertical-centering is intentionally dropped per design; the
// prop stays declared for caller-API compatibility but no longer centers.
const dialogRootClass = computed(() =>
    cn(
        "border-0 bg-transparent shadow-none p-0 m-0 w-full md:mx-auto",
        maxWidthClass.value,
    ),
);

// BaseModal is the only reader of modalTeleportTarget() — every wrapper (BaseModalSimple,
// AuthModal, StampPlacementModal, ...) just forwards its own teleportTo prop down here.
const resolvedTeleportTo = computed(() => props.teleportTo ?? modalTeleportTarget());

</script>

<template>
    <!--
      Outer Teleport handles DOM placement so Dialog's own Portal can be set to
      appendTo="self" (inline). This keeps PrimeVue's focus-trap, Escape handling,
      body-scroll lock, and mask all working while avoiding Portal's async-mounted
      delay (which would otherwise hide content from synchronous test assertions).
      Dialog's close paths (mask click, Escape) route through update:visible → close emit.
    -->
    <Teleport :to="resolvedTeleportTo">
        <Dialog
            :visible="show"
            modal
            :closable="false"
            :draggable="false"
            :dismissable-mask="closeable"
            :close-on-escape="closeable"
            append-to="self"
            :aria-label="ariaLabelledby ? null : ariaLabel"
            :aria-labelledby="ariaLabelledby"
            :pt="{
                root: { class: dialogRootClass },
                // !items-start: PrimeVue sets the mask's align-items:center as an INLINE
                // style (position-derived), which a plain utility class can't override; the
                // !important utility wins by cascade, top-anchoring the dialog vertically.
                mask: { class: '!items-start p-4 md:p-10 md:py-15 overflow-y-auto' },
            }"
            @update:visible="(v) => !v && close()"
        >
            <template #container>
                <!-- The panel (chrome, header row, content padding) is shared with
                     ConfirmActionDialog so both surfaces match by construction. -->
                <BaseModalPanel
                    :title="title"
                    :title-in-body="titleInBody"
                    :closeable="closeable"
                    :class="props.class"
                    @close="close"
                >
                    <template
                        v-if="$slots.title"
                        #title
                    >
                        <slot name="title" />
                    </template>
                    <template #header>
                        <slot name="header" />
                    </template>
                    <slot />
                    <template #footer>
                        <slot name="footer" />
                    </template>
                </BaseModalPanel>
            </template>
        </Dialog>
    </Teleport>
</template>
